import { useState } from "react";
import { ArrowRight, Loader2, CheckCircle2, AlertCircle } from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api/demo-request`;

const inputClass =
    "w-full rounded-xl border border-white/[0.1] bg-[#0B0F17]/70 px-4 py-3 text-sm text-white placeholder:text-[#6B7280] outline-none transition-colors duration-200 focus:border-emerald-500/60";

export const FinalCTA = () => {
    const [form, setForm] = useState({ name: "", company: "", email: "", message: "", website: "" });
    const [state, setState] = useState("idle");
    const [error, setError] = useState("");

    const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setState("sending");
        setError("");
        try {
            const res = await fetch(API_URL, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(form),
            });
            if (!res.ok) throw new Error(await res.text());
            setState("success");
        } catch {
            setState("error");
            setError("Das hat leider nicht geklappt. Bitte versuchen Sie es erneut – oder schreiben Sie direkt an info@kirenzo.de.");
        }
    };

    return (
        <section id="demo" className="py-20 lg:py-28 border-t border-white/[0.06] scroll-mt-16" aria-labelledby="cta-heading">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="relative overflow-hidden rounded-3xl border border-emerald-500/25 bg-gradient-to-b from-[#161F30] to-[#0B0F17] p-8 sm:p-14 lg:p-20">
                    <div
                        className="pointer-events-none absolute -top-24 right-0 h-72 w-72 rounded-full opacity-40"
                        style={{ background: "radial-gradient(closest-side, rgba(16,185,129,0.25), transparent)" }}
                        aria-hidden="true"
                    />
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
                        <div>
                            <p className="font-mono-label text-xs uppercase tracking-[0.2em] text-emerald-400">Nächster Schritt</p>
                            <h2 id="cta-heading" className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-white">
                                15 Minuten für eine unverbindliche Demo.
                            </h2>
                            <p className="mt-4 text-base sm:text-lg text-[#9CA3AF] leading-relaxed">
                                Wir zeigen Ihnen an einem echten Beispiel aus Ihrem Arbeitsalltag, welche Prozesse
                                sich automatisieren lassen und was Ihnen das konkret bringt. Kein Vertriebsdruck,
                                kein Foliengewitter – Sie sprechen direkt mit den Gründern, und wenn es nicht
                                passt, sagen wir das auch.
                            </p>
                            <p className="mt-6 text-sm text-[#6B7280]">
                                Lieber direkt schreiben?{" "}
                                <a href="mailto:info@kirenzo.de" data-testid="final-cta-email-link" className="text-emerald-400 hover:text-emerald-300 underline underline-offset-4">
                                    info@kirenzo.de
                                </a>
                            </p>
                        </div>
                        {state === "success" ? (
                            <div data-testid="demo-form-success" className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-8 text-center">
                                <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-400" aria-hidden="true" />
                                <h3 className="mt-4 text-xl font-medium text-white">Anfrage ist raus.</h3>
                                <p className="mt-2 text-sm text-[#9CA3AF] leading-relaxed">
                                    Danke, {form.name.split(" ")[0]}! Wir melden uns innerhalb von 24 Stunden bei
                                    Ihnen – versprochen.
                                </p>
                            </div>
                        ) : (
                            <form data-testid="demo-form" onSubmit={submit} className="rounded-2xl border border-white/[0.08] bg-[#0B0F17]/60 p-6 sm:p-8 space-y-4">
                                <div>
                                    <label htmlFor="demo-name" className="mb-1.5 block text-sm font-medium text-white">Ihr Name *</label>
                                    <input id="demo-name" data-testid="demo-form-name-input" type="text" required minLength={2} maxLength={100}
                                        value={form.name} onChange={update("name")} placeholder="Max Mustermann" className={inputClass} />
                                </div>
                                <div>
                                    <label htmlFor="demo-company" className="mb-1.5 block text-sm font-medium text-white">Unternehmen *</label>
                                    <input id="demo-company" data-testid="demo-form-company-input" type="text" required minLength={2} maxLength={150}
                                        value={form.company} onChange={update("company")} placeholder="Muster GmbH" className={inputClass} />
                                </div>
                                <div>
                                    <label htmlFor="demo-email" className="mb-1.5 block text-sm font-medium text-white">E-Mail *</label>
                                    <input id="demo-email" data-testid="demo-form-email-input" type="email" required
                                        value={form.email} onChange={update("email")} placeholder="max@muster.de" className={inputClass} />
                                </div>
                                <div>
                                    <label htmlFor="demo-message" className="mb-1.5 block text-sm font-medium text-white">
                                        Was frisst bei Ihnen Zeit? <span className="text-[#6B7280] font-normal">(optional)</span>
                                    </label>
                                    <textarea id="demo-message" data-testid="demo-form-message-input" rows={3} maxLength={2000}
                                        value={form.message} onChange={update("message")}
                                        placeholder="Z. B.: Wir beantworten täglich dutzende Statusanfragen per Mail …"
                                        className={`${inputClass} resize-none`} />
                                </div>
                                <input type="text" name="website" value={form.website} onChange={update("website")}
                                    tabIndex={-1} autoComplete="off" aria-hidden="true"
                                    className="absolute left-[-9999px] h-0 w-0 opacity-0" />
                                {state === "error" && (
                                    <p data-testid="demo-form-error" role="alert" className="flex items-start gap-2 text-sm text-red-400">
                                        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                                        {error}
                                    </p>
                                )}
                                <button
                                    type="submit"
                                    data-testid="demo-form-submit-button"
                                    disabled={state === "sending"}
                                    className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-emerald-500 px-7 py-3.5 text-base font-semibold text-[#0B0F17] transition-all duration-200 hover:bg-emerald-600 hover:shadow-[0_0_32px_rgba(16,185,129,0.35)] disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                    {state === "sending" ? (
                                        <>
                                            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                                            Wird gesendet …
                                        </>
                                    ) : (
                                        <>
                                            Kostenlose Demo anfragen
                                            <ArrowRight className="h-4 w-4" aria-hidden="true" />
                                        </>
                                    )}
                                </button>
                                <p className="text-xs text-[#6B7280] leading-relaxed">
                                    Mit dem Absenden stimmen Sie zu, dass wir Ihre Angaben zur Beantwortung der
                                    Anfrage verarbeiten. Details in der{" "}
                                    <a href="/datenschutz" data-testid="demo-form-privacy-link" className="underline underline-offset-4 hover:text-white">
                                        Datenschutzerklärung
                                    </a>
                                    .
                                </p>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};
